// src/components/Auth/SignUp.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  
  // Redirect if user is already logged in or signup was successful
  useEffect(() => {
    if (user && !success) {
      navigate('/');
    }
  }, [user, success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    // Password complexity requirements
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (!/(?=.*[0-9!@#$%^&*])/.test(password)) {
      setError('Password must contain at least one number or special character');
      return;
    }
    
    if (!termsAccepted) {
      setError('You must accept the Terms of Service and Privacy Policy');
      return;
    }
    
    try {
      setError('');
      setMessage('');
      setLoading(true);
      
      const { error } = await signUp(email, password);
      
      if (error) throw error;
      
      // If email confirmation is enabled, show message to check email
      setMessage('Please check your email to confirm your account.');
      setSuccess(true);
      
      // If auto-confirm is enabled, user will be signed in automatically
      // and redirected by the useEffect hook
    } catch (error) {
      setError(error.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px',
    margin: '8px 0',
    display: 'inline-block',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxSizing: 'border-box',
  };

  const buttonStyle = {
    width: '100%',
    backgroundColor: '#2980B9',
    color: 'white',
    padding: '14px 20px',
    margin: '8px 0',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  };

  const errorStyle = {
    color: 'red',
    marginBottom: '10px',
    textAlign: 'center',
  };

  const messageStyle = {
    color: 'green',
    marginBottom: '10px',
    textAlign: 'center',
  };

  return (
    <div>
      <h2 style={{ textAlign: 'center' }}>Sign Up</h2>
      
      {error && <div style={errorStyle}>{error}</div>}
      {message && <div style={messageStyle}>{message}</div>}
      
      <form onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email">Email</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            style={inputStyle}
            required
          />
        </div>
        
        <div>
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            style={inputStyle}
            required
          />
        </div>
        
        <div>
          <label htmlFor="confirm-password">Confirm Password</label>
          <input
            type="password"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            style={inputStyle}
            required
          />
        </div>
        
        <div style={{ margin: '15px 0' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              style={{ marginTop: '4px' }}
            />
            <span style={{ fontSize: '0.9em' }}>
              I accept the <Link to="/terms" target="_blank" style={{ color: '#2980B9' }}>Terms of Service</Link> and <Link to="/privacy-policy" target="_blank" style={{ color: '#2980B9' }}>Privacy Policy</Link>
            </span>
          </label>
        </div>
        
        <button 
          type="submit" 
          disabled={loading}
          style={buttonStyle}
        >
          {loading ? 'Creating account...' : 'Sign Up'}
        </button>
      </form>
    </div>
  );
};

export default SignUp;